import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon, DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { type IncomeEntry, type OtherIncomeFormType, otherIncomesFormSchema } from "Forms/Schemas/Budget/Incomes/otherIncomesFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getListIncomesQueryKey,
  useCreateIncome,
  useDeleteIncome,
  useGetIncome,
  useUpdateIncome,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Income, IncomeAmountAmountType, IncomeBodyIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";

type Props = {
  onClose: () => void;
  clientId?: string;
  edit?: Income;
};

export const OtherIncomeForm = forwardRef<{}, Props>(({ onClose, clientId, edit }, ref) => {
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const queryClient = useQueryClient();
  const { data: editIncomeData } = useGetIncome(analysisId, versionId, edit?.guid ?? "", {
    query: { enabled: !!edit },
  });
  const { mutate: putIncome } = useUpdateIncome();
  const { mutate } = useCreateIncome();
  const { mutate: deleteIncome } = useDeleteIncome();

  const { control, handleSubmit } = useForm<OtherIncomeFormType>({
    resolver: yupResolver(otherIncomesFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      incomes: [{ incomeName: "", amountValue: undefined }],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "incomes",
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteIncome: () => {
      handleDelete();
    },
  }));

  useEffect(() => {
    if (editIncomeData && edit) {
      replace(
        editIncomeData.incomeAmounts.map((income) => ({
          incomeName: income.name,
          amountValue: income.amount.value.toString() || ("" as any),
        })),
      );
    }
  }, [editIncomeData, edit, replace]);

  const onSubmit = async (data: OtherIncomeFormType) => {
    if (edit) {
      putIncome(
        {
          analysisId,
          versionId,
          incomeId: edit.guid,
          data: {
            incomeType: IncomeBodyIncomeType.other,
            clientGuid: clientId ?? "",
            incomeAmounts: data.incomes.map((income: IncomeEntry) => ({
              amountType: IncomeAmountAmountType.other,
              amount: {
                value: income.amountValue ?? 0,
                currencyCode: "CZK",
                frequency: AmountFrequency.monthly,
              },
              name: income.incomeName,
            })),
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    } else {
      mutate(
        {
          data: {
            incomeType: IncomeBodyIncomeType.other,
            clientGuid: clientId ?? "",
            incomeAmounts: data.incomes.map((income: IncomeEntry) => ({
              amountType: IncomeAmountAmountType.other,
              amount: {
                value: income.amountValue ?? 0,
                currencyCode: "CZK",
                frequency: AmountFrequency.monthly,
              },
              name: income.incomeName,
            })),
          },
          analysisId,
          versionId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    }
  };

  const handleDelete = () => {
    deleteIncome(
      {
        analysisId,
        versionId,
        incomeId: edit?.guid ?? "",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
        },
      },
    );
  };

  const addNewIncome = () => {
    append({ incomeName: "", amountValue: undefined as any });
  };

  return (
    <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
      <Box>
        <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
          {t(Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Other)}
        </Typography>

        {fields.map((field, index) => (
          <Grid container spacing={Spacing.XL} key={field.id} mb={Spacing.XL}>
            <Grid size={5}>
              <FormTextField
                control={control}
                name={`incomes.${index}.incomeName`}
                label={t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.IncomeName)}
                required
                disabled={readOnly}
                placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
                fullWidth
              />
            </Grid>

            <Grid size={5}>
              <FormNumberField
                control={control}
                name={`incomes.${index}.amountValue`}
                label={t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.IncomeAmount)}
                required
                valueType="text"
                scale={2}
                min={0}
                thousandsSeparator=" "
                unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                disabled={readOnly}
                placeholder="0"
                fullWidth
              />
            </Grid>

            <Grid size={1} display="flex" alignItems="flex-start" mt={5} justifyContent="center">
              <Button variant="text" color="error" onClick={() => remove(index)} disabled={fields.length === 1 || readOnly}>
                <DeleteIcon />
              </Button>
            </Grid>
          </Grid>
        ))}

        <Button variant="text" startIcon={<AddIcon />} disabled={readOnly} onClick={addNewIncome}>
          {t(Resources.AnalysisDetail.Budget.Incomes.Form.Buttons.AddAnother)}
        </Button>
      </Box>
    </Stack>
  );
});
